import axios from 'axios'

import type { components, operations } from '../constants/schema'

type GetProductSaleListParams = Required<operations['ProductController_findAllProductSales']['parameters']>['query']
type GetProductSellHistoryListParams = Required<
  operations['ProductController_findAllProductSellHistory']['parameters']
>['query']
type GetHoldingProductListParams = Required<
  operations['UserController_getCurrentUserProductHolding']['parameters']
>['query']

/**
 * 청약 예정/청약 중 작품 목록
 * @param size call 한 번에 불러오는 개수
 * @param offset pagination
 * @param sort 정렬 방법
 * @returns ResListProduct
 */
export const getProductSaleList = async ({ size = 10, offset = 0, sort }: GetProductSaleListParams) => {
  return (
    await axios.get<components['schemas']['ResListProduct']>('/product/sales', { params: { size, offset, sort } })
  ).data
}

/**
 * 매각 작품 평균 데이터
 * @returns ProductSellHistorySummaryDto
 */
export async function getProductSellHistorySummary() {
  return (await axios.get<components['schemas']['ProductSellHistorySummaryDto']>('/product/sellHistory/summary')).data
}

/**
 * 매각 작품 목록
 * @param size call 한 번에 불러오는 개수
 * @param offset pagination
 * @param sort 정렬 방법
 * @returns ResListProductSellHistory
 */
export async function getProductSellList({ size = 10, offset = 0, sort }: GetProductSellHistoryListParams) {
  return (
    await axios.get<components['schemas']['ResListProductSellHistory']>('/product/sellHistory', {
      params: { size, offset, sort },
    })
  ).data
}

/**
 * 작품(청약) 정보 상세 불러오기
 * @param productId 작풍 id
 * @returns ProductDetailDto
 */
export const getProductDetail = async (productId: number) => {
  return (await axios.get<components['schemas']['ProductDetailDto']>(`/product/${productId}`)).data
}

/**
 * 작품 청약
 * @param productId 작품 id
 * @param data ReqQueryProductPurchase
 * @returns
 */
export const purchaseProduct = async ({
  productId,
  data,
}: {
  productId: number
  data: components['schemas']['ReqQueryProductPurchase']
}) => {
  try {
    return (await axios.post<components['schemas']['UserProductDto']>(`/product/${productId}/purchase`, data)).data
  } catch (error) {
    throw error
  }
}

/**
 * 작품 청약 취소
 * @param productId 작품 id
 */
export const cancelProduct = async (productId: number) => {
  try {
    await axios.post(`/product/${productId}/cancel`)
  } catch (error) {
    throw error
  }
}

/**
 * 로그인 유저 보유중인 작품 목록
 * @param size call 한 번에 불러오는 개수
 * @param offset pagination
 * @param sort 정렬 방법
 * @returns ResListUserProduct
 */
export const getHoldingProductList = async ({ size = 10, offset = 0, sort }: GetHoldingProductListParams) => {
  return (
    await axios.get<components['schemas']['ResListUserProduct']>('/user/current/product/holding', {
      params: { size, offset, sort },
    })
  ).data
}

/**
 * 로그인 유저 보유중인 작품 상세 조회
 * @param userProductId 유저 작품 id
 * @returns UserProductDto
 */
export const getUserProductDetail = async (userProductId: number) => {
  return (await axios.get<components['schemas']['UserProductDto']>(`/user/product/${userProductId}`)).data
}

/**
 * 로그인 유저 보유중인 작품 투자내역 조회
 * @param userProductId 유저 작품 id
 * @returns PurchaseLogDto
 */
export const getUserProductPurchases = async (userProductId: number) => {
  return (await axios.get<components['schemas']['PurchaseLogDto'][]>(`/user/product/${userProductId}/purchases`)).data
}

/**
 * 로그인 유저 보유중인 작품 매각 정보 조회
 * @param userProductId 유저 작품 id
 * @returns UserProductSettleInformationDto
 */
export const getUserProductSell = async (userProductId: number) => {
  return (
    await axios.get<components['schemas']['UserProductSettlementDto']>(`/user/product/${userProductId}/info/sell`)
  ).data
}

/**
 * 로그인 유저 보유중인 작품 정산 정보 조회
 * @param userProductId 유저 작품 id
 * @returns UserProductSettleInformationDto
 */
export const getUserProductSettle = async (userProductId: number) => {
  return (
    await axios.get<components['schemas']['UserProductSettlementDto']>(`/user/product/${userProductId}/info/settle`)
  ).data
}

export type { GetProductSaleListParams, GetProductSellHistoryListParams, GetHoldingProductListParams }
